import ModalPromotion from '@/components/sections/builder/elements/body/cart-total/component/modal-promotion';
import { ElementProps } from '@/components/sections/builder/types';
import { WrapperChild } from '@/components/sections/builder/wrapper';
import { Button } from '@/components/ui/button';
import { useGetListPromotions } from '@/hooks/api/storefront/promotion';
import useModal from '@/hooks/useModal';
import { CheckCircle2, GiftIcon, Ticket, Trash2 } from 'lucide-react';
import Cookies from 'js-cookie';
import {
  calculateTotalDiscountPerPriority,
  cn,
  formatIDR,
  generatePromotionsPayload,
  truncateText,
} from '@/lib/utils';
import {
  BasePromotion,
  BasePromotionRewardProduct,
  BasePromotionRewardProductItem,
} from '@/types/http/promotion/common';
import ModalGiftReward from '@/components/sections/builder/elements/body/cart-total/component/modal-gift-reward';
import { Skeleton } from '@/components/ui/skeleton';
import CardVoucher from '@/components/sections/builder/elements/body/cart-total/component/card-voucher';
import DynamicImage from '@/components/commons/dynamic-image';
import { useEffect, useMemo, useState } from 'react';
import { usePromotionStore } from '@/stores/promotion';
import { shallow } from 'zustand/shallow';
import { ThemeType, useBuilderStore } from '@/stores/builder';
import Link from 'next/link';
import FixedCheckoutButton from '@/components/sections/builder/elements/body/cart-total/component/fixed-checkout-button';
import ModalAlert from '@/components/sections/modal-alert';
import { toast } from '@/components/ui/use-toast';
import { BaseCart } from '@/types/http/cart/common';
import { Card, CardContent } from '@/components/ui/card';
import { dummyPromotions } from '@/constant/dummyData/dummy-carts';

export default function ElementContent({
  element,
  data,
  preview,
  theme,
  isLoadingData,
}: ElementProps) {
  const childs = element.childs || [];
  const modalPromotion = useModal();
  const modalGift = useModal();
  const modalOverlap = useModal();
  const token = Cookies.get('accessToken') || '';

  const [promoReward, setPromoReward] = useState<BasePromotion | null>(null);
  const [maxQtyReward, setMaxQtyReward] = useState<number>(0);
  const [selectedTab, setSelectedTab] = useState<'product_group' | 'product_variant'>(
    'product_group'
  );
  const [pendingVoucher, setPendingVoucher] = useState<{
    promoRewards: BasePromotion[];
    code: string;
  } | null>(null);

  const promotionStore = usePromotionStore(
    (state) => ({
      selectedPromotion: state.selectedPromotion,
      addPromotion: state.addPromotion,
      removePromotion: state.removePromotion,
      applyVoucherRewards: state.applyVoucherRewards,
      applyVoucher: state.applyVoucher,
      applyPromotion: state.applyPromotion,
      syncFreeGiftPromotion: state.syncFreeGiftPromotion,
      selectedFreeGift: state.selectedFreeGift,
      removeFreeGift: state.removeFreeGift,
      replaceFreeGift: state.replaceFreeGift,
      removeFreeGiftByItemId: state.removeFreeGiftByItemId,
    }),
    shallow
  );
  const themeBuilder = useBuilderStore((state) => state.theme, shallow);
  const themeLayout = preview ? themeBuilder : theme;
  const promotionTheme = themeLayout?.promotion;

  const payloadListPromotion = useMemo(() => {
    const items = (data?.items as BaseCart[]) || [];

    const { mappedItems, grandTotal } = items.reduce(
      (acc, item) => {
        const discountPrice = item.discount_price ?? 0;
        const discountAmount = item.discount_amount ?? 0;
        const sellPrice = item.sell_price;

        const pricePerItem = discountPrice > 0 ? discountPrice : sellPrice;
        const itemTotal = item.quantity * pricePerItem;

        acc.mappedItems.push({
          item_id: item.id,
          qty: item.quantity,
          amount: itemTotal,
        });

        acc.grandTotal += itemTotal - discountAmount;

        return acc;
      },
      {
        mappedItems: [] as {
          item_id: string;
          qty: number;
          amount: number;
        }[],
        grandTotal: 0,
      }
    );

    return {
      items: mappedItems,
      grand_total: grandTotal,
      current_date: new Date().toISOString(),
    };
  }, [data?.items]);

  const { data: recommendPromotion, isLoading: isLoadingRecommend } = useGetListPromotions(
    payloadListPromotion,
    true,
    token,
    {
      enabled: !!payloadListPromotion.items?.length,
    }
  );

  const { data: listPromotion } = useGetListPromotions(payloadListPromotion, false, token, {
    enabled: !!payloadListPromotion.items?.length,
  });

  const allPromotions = useMemo(() => {
    const selectedIds = new Set(
      promotionStore.selectedPromotion.map((promo) => promo.promotion_id)
    );
    const merged = [...promotionStore.selectedPromotion];

    listPromotion?.forEach((promo) => {
      if (!selectedIds.has(promo.promotion_id)) {
        merged.push(promo);
      }
    });

    return merged;
  }, [promotionStore.selectedPromotion, listPromotion]);

  const listPromotionDiscount = useMemo(() => {
    return promotionStore.selectedPromotion
      ?.filter((promo) => Array.isArray(promo.discount_reward) && promo.discount_reward.length > 0)
      .sort((a, b) => Number(a.priority) - Number(b.priority));
  }, [promotionStore.selectedPromotion]);

  const listPromotionFreeGift = useMemo(() => {
    return promotionStore.selectedPromotion.some(
      (promo) => promo.product_reward && promo.product_reward.length > 0
    );
  }, [promotionStore.selectedPromotion]);

  const { updatedPromos, totalDiscount } = calculateTotalDiscountPerPriority(
    listPromotionDiscount,
    preview ? 500000 : payloadListPromotion.grand_total
  );

  const handleApplyPromotion = (promo: BasePromotion) => {
    promotionStore.addPromotion(promo, payloadListPromotion.grand_total);
  };

  const handleRemovePromotion = (promoId: string) => {
    promotionStore.removePromotion(promoId);
  };

  const handleApplyVoucher = (voucherIds: string[]) => {
    if (!allPromotions) return;

    promotionStore.applyVoucher(voucherIds, allPromotions);
  };

  const detectPromotionConflict = (existingPromos: BasePromotion[], newPromos: BasePromotion[]) => {
    const hasExistingNonOverlap = existingPromos.some((p) => !p.is_overlap);
    const hasNewNonOverlap = newPromos.some((p) => !p.is_overlap);
    const allExistingOverlap = existingPromos.every((p) => p.is_overlap);

    return (allExistingOverlap && hasNewNonOverlap) || (hasExistingNonOverlap && !hasNewNonOverlap);
  };

  const handleSubmitRedeemVoucher = (promoRewards: BasePromotion[], code: string) => {
    const isNewOverlapFalse = promoRewards.some((promo) => promo.is_overlap === false);
    const hasConflict = detectPromotionConflict(
      promotionStore.selectedPromotion || [],
      promoRewards
    );

    if (hasConflict) {
      setPendingVoucher({ promoRewards, code });
      modalOverlap.setIsShow(true);
      return;
    }

    promotionStore.applyVoucherRewards(promoRewards, code, isNewOverlapFalse);
  };

  const handleConfirmOverlap = () => {
    if (pendingVoucher) {
      const isNewOverlapFalse = pendingVoucher.promoRewards.some((p) => p.is_overlap === false);
      promotionStore.applyVoucherRewards(
        pendingVoucher.promoRewards,
        pendingVoucher.code,
        isNewOverlapFalse
      );
    }
    modalOverlap.setIsShow(false);
    setPendingVoucher(null);
    toast({
      title: 'Success',
      variant: 'success',
      description: 'Promotion has been applied',
    });
  };

  useEffect(() => {
    if (!listPromotion || !listPromotion.length || preview) return;

    if (!isLoadingData) {
      const recommendedPromo = recommendPromotion?.find(
        (promo) => payloadListPromotion.grand_total >= promo.minimum_spending
      );
      const fallbackPromo = listPromotion
        ?.filter((promo) => payloadListPromotion.grand_total >= promo.minimum_spending)
        .sort((a, b) => b.minimum_spending - a.minimum_spending)[0];

      const promoToApply = recommendedPromo || fallbackPromo;
      if (!promoToApply) return;

      const selectedPromos = promotionStore.selectedPromotion || [];
      const allSelectedOverlapTrue = selectedPromos.every((promo) => promo.is_overlap === true);
      const newPromoOverlapTrue = promoToApply.is_overlap === true;

      if (allSelectedOverlapTrue && newPromoOverlapTrue) {
        promotionStore.addPromotion(promoToApply, payloadListPromotion.grand_total, listPromotion);
      } else {
        promotionStore.applyPromotion(promoToApply);
      }
    }
  }, [
    listPromotion,
    recommendPromotion,
    payloadListPromotion.grand_total,
    data?.promotions,
    isLoadingData,
  ]);

  useEffect(() => {
    promotionStore.syncFreeGiftPromotion();
  }, [promotionStore.selectedPromotion]);

  return (
    <>
      <section className='theme_cart_page_bg'>
        <div
          className={cn(
            'mx-auto my-2 px-4 duration-100',
            (themeLayout?.layout?.pageWidth ?? 0) < 800
              ? 'theme_layout_page_width'
              : 'max-w-[1000px]'
          )}
          style={
            {
              '--background': promotionTheme?.colorScheme?.background,
              '--btn-outline': promotionTheme?.colorScheme?.btnOutline,
            } as React.CSSProperties
          }
        >
          {(data?.items?.length > 0 || preview) && (
            <div className='relative flex w-full flex-col gap-2 overflow-hidden'>
              <div className='relative mb-8 flex w-full flex-col items-start gap-4'>
                {/* List promotion discount usage */}
                {(listPromotionDiscount.length > 0 || preview) && (
                  <div className='flex w-full flex-col'>
                    <div className='mb-2 flex items-center justify-between gap-2'>
                      <div
                        className='flex items-center gap-2'
                        style={{ color: promotionTheme?.colorScheme?.text }}
                      >
                        <CheckCircle2 />
                        <p className='text-sm md:text-base'>
                          YOU SAVED {formatIDR(totalDiscount)} ON THIS ORDER
                        </p>
                      </div>
                      <Button
                        variant='link'
                        className='items-center p-0 font-normal'
                        style={{
                          color: promotionTheme?.colorScheme?.textSecondary,
                        }}
                        onClick={() => modalPromotion.open()}
                      >
                        <span className='text-sm'>See all offers</span>
                      </Button>
                    </div>

                    <div className='grid w-full grid-cols-1 gap-2'>
                      {(preview ? dummyPromotions : updatedPromos).map((promo, index) => {
                        return (
                          <CardVoucher
                            key={index}
                            theme={promotionTheme}
                            themeCart={themeLayout?.cart?.pageCartColorScheme}
                            promo={
                              {
                                ...promo,
                                total_discount: promo.total_discount ?? 0,
                              } as BasePromotion & { total_discount: number }
                            }
                            minSpending={promo.minimum_spending}
                            grandTotal={payloadListPromotion.grand_total}
                            onApply={handleApplyPromotion}
                            onRemove={handleRemovePromotion}
                            isRemove={!preview}
                            isShowReward
                          />
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* List promotion available */}
                <div className='mb-2 flex w-full items-center justify-between gap-2'>
                  <div
                    className='flex items-center gap-2'
                    style={{ color: promotionTheme?.colorScheme?.text }}
                  >
                    <Ticket />
                    <p className='text-sm md:text-base'>ADD MORE TO SAVE MORE</p>
                  </div>
                  {!preview && !listPromotionDiscount.length && (
                    <Button
                      variant='link'
                      className='items-center p-0 font-normal'
                      style={{
                        color: promotionTheme?.colorScheme?.textSecondary,
                      }}
                      onClick={() => modalPromotion.open()}
                    >
                      <span className='text-sm'>See all offers</span>
                    </Button>
                  )}
                </div>
                <div className='relative flex w-full flex-col gap-4 lg:gap-10'>
                  {isLoadingRecommend ? (
                    <div className='flex flex-col gap-1.5'>
                      <Skeleton className='h-20 w-full' />
                    </div>
                  ) : (preview ? dummyPromotions : recommendPromotion) &&
                    (preview ? dummyPromotions : recommendPromotion)!.length > 0 ? (
                    (preview ? dummyPromotions : recommendPromotion)!.map((promo, index) => {
                      const grandTotal = preview ? 500000 : payloadListPromotion.grand_total;
                      const minSpending = promo.minimum_spending;
                      const percentage = Math.min((grandTotal / minSpending) * 100, 100);

                      return (
                        <CardVoucher
                          key={index}
                          theme={promotionTheme}
                          themeCart={themeLayout?.cart?.pageCartColorScheme}
                          promo={promo as BasePromotion}
                          percentage={percentage}
                          minSpending={minSpending}
                          grandTotal={grandTotal}
                          isEligible
                        />
                      );
                    })
                  ) : (
                    <Card className='border-2 border-dashed border-gray-200 bg-gray-50/50 p-2 text-sm'>
                      <CardContent className='flex items-center gap-3 p-3'>
                        <p className='text-gray-600'>
                          No recommended promotions. In the meantime, check out all available
                          offers.
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {(listPromotionFreeGift || preview) && (
                  <div className='relative flex w-full flex-col'>
                    <div
                      className='mb-2 flex items-center gap-2'
                      style={{ color: promotionTheme?.colorScheme?.text }}
                    >
                      <GiftIcon />
                      <p className='text-sm md:text-base'>YOUR FREE GIFT</p>
                    </div>
                    <div className='flex w-full flex-col gap-2 rounded-xl border border-gray-200 p-3'>
                      {(preview ? dummyPromotions : promotionStore.selectedPromotion).map(
                        (promo) => {
                          const rewards = promotionStore.selectedFreeGift.filter(
                            (gift) => gift.promotion_id === promo.promotion_id
                          );

                          return promo.product_reward && promo.product_reward.length > 0 ? (
                            <div key={promo.promotion_id} className='relative flex flex-col gap-2'>
                              <div className='flex flex-col items-start justify-between md:flex-row md:items-center'>
                                <span
                                  className='text-sm font-medium lg:text-base'
                                  style={{
                                    color: promotionTheme?.colorScheme?.text,
                                  }}
                                >
                                  {promo.promotion_name}
                                </span>
                                <Button
                                  className={cn(
                                    'p-1 text-xs font-normal transition-all lg:px-4',
                                    'border border-[--btn-outline] bg-transparent text-[--btn-outline] hover:opacity-80'
                                  )}
                                  variant={null}
                                  onClick={() => {
                                    modalGift.open();
                                    setPromoReward(promo as BasePromotion);
                                    setMaxQtyReward(
                                      promo.product_reward && promo.product_reward[0]
                                        ? promo.counted_multiply
                                          ? promo.product_reward[0].qty * promo.counted_multiply
                                          : promo.product_reward[0].qty
                                        : 0
                                    );
                                  }}
                                  disabled={preview}
                                >
                                  {rewards.length > 0 ? 'Change Gift' : 'Choose Gift'}
                                </Button>
                              </div>
                              {rewards.length > 0 &&
                                rewards.map((reward) =>
                                  reward.items.map((item) => (
                                    <div
                                      key={item.item_id}
                                      className='relative flex flex-row items-center justify-between gap-2'
                                    >
                                      <div className='flex min-w-0 gap-2'>
                                        <DynamicImage
                                          src={
                                            (item.product_assets?.[0] as string) ||
                                            String(reward.product_group.product_assets?.[0])
                                          }
                                          alt={item.product_name as string}
                                          width={500}
                                          height={500}
                                          className='aspect-square h-[70px] w-[70px] rounded border object-contain'
                                        />

                                        <div className='flex w-full min-w-0 flex-col gap-1 overflow-hidden text-sm font-normal text-gray-600'>
                                          <span
                                            className='line-clamp-1 break-words'
                                            title={`${reward.product_group.product_name} ${
                                              item.is_variants ? `- ${item.item_name}` : ''
                                            }`}
                                          >
                                            {truncateText(
                                              reward.product_group.product_name as string,
                                              30
                                            )}{' '}
                                            {item?.is_variants &&
                                              `- ${truncateText(item.item_name as string, 25)}`}
                                          </span>
                                          <div className='flex items-center'>
                                            <Link
                                              href={`/products/${item.slug}`}
                                              className='text-xs text-primary underline'
                                              target='_blank'
                                            >
                                              View Product
                                            </Link>
                                          </div>
                                          <div className='flex items-center gap-1 lg:hidden'>
                                            <span>x{item.qty}</span>
                                            <Button
                                              type='button'
                                              variant='secondary'
                                              size='icon'
                                              className='text-xs text-red-500 hover:text-red-700'
                                              onClick={() => {
                                                promotionStore.removeFreeGiftByItemId(
                                                  reward.promotion_id as string,
                                                  item.item_id
                                                );
                                              }}
                                            >
                                              <Trash2 className='size-4' />
                                            </Button>
                                          </div>
                                        </div>
                                      </div>

                                      <div className='hidden items-center gap-3 lg:flex'>
                                        <span>x{item.qty}</span>
                                        <Button
                                          type='button'
                                          variant='secondary'
                                          size='icon'
                                          className='text-xs text-red-500 hover:text-red-700'
                                          onClick={() => {
                                            promotionStore.removeFreeGiftByItemId(
                                              reward.promotion_id as string,
                                              item.item_id
                                            );
                                          }}
                                        >
                                          <Trash2 className='size-4' />
                                        </Button>
                                      </div>
                                    </div>
                                  ))
                                )}
                            </div>
                          ) : null;
                        }
                      )}
                      {(promotionStore.selectedPromotion.some(
                        (promo) =>
                          !promotionStore.selectedFreeGift.find(
                            (gift) => gift.promotion_id === promo.promotion_id
                          )
                      ) ||
                        preview) && <span>Don&apos;t want free gift? Feel free to skip.</span>}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {!themeLayout?.cart?.fixedCheckoutButton && (
            <div className='grid-cols-1 md:grid'>
              <div className='flex flex-col items-end text-right'>
                {childs.map((child) => (
                  <WrapperChild
                    child={{
                      ...child,
                      parentData: {
                        ...data,
                        promotions_payload: {
                          cart_id: data?.cartId,
                          promotions: generatePromotionsPayload(
                            promotionStore.selectedPromotion,
                            promotionStore.selectedFreeGift.filter(
                              (gift) => gift.promotion_id !== undefined
                            ) as {
                              promotion_id: string;
                              product_group: BasePromotionRewardProduct;
                              items: (BasePromotionRewardProductItem & {
                                item_id: string;
                                qty?: number;
                              })[];
                            }[]
                          ),
                        },
                      },
                      theme: themeLayout,
                    }}
                    key={child.id}
                    parentType={element.type}
                    preview={preview}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {themeLayout?.cart?.fixedCheckoutButton && (
        <FixedCheckoutButton theme={themeLayout as ThemeType} cart={data} preview={preview} />
      )}

      <ModalPromotion
        openModal={modalPromotion.isShow}
        setOpenModal={modalPromotion.setIsShow}
        grandTotal={preview ? 1000000 : payloadListPromotion.grand_total}
        listPromotion={preview ? dummyPromotions : allPromotions}
        onApplyVoucher={handleApplyVoucher}
        onRedeemVoucher={handleSubmitRedeemVoucher}
        token={token}
        data={data}
        theme={promotionTheme}
        preview={preview}
      />

      <ModalGiftReward
        openModal={modalGift.isShow}
        promotion={promoReward as BasePromotion}
        token={token}
        maxQty={maxQtyReward}
        setOpenModal={modalGift.setIsShow}
        setPromotion={setPromoReward}
        onSaveGift={(data) => {
          promotionStore.replaceFreeGift(promoReward?.promotion_id as string, data);
        }}
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        colorScheme={promotionTheme?.colorScheme}
        initialSelectedProducts={promotionStore.selectedFreeGift.filter(
          (gift) => gift.promotion_id === promoReward?.promotion_id
        )}
      />

      <ModalAlert
        openModal={modalOverlap.isShow}
        setOpenModal={modalOverlap.setIsShow}
        title='Are you sure'
        description='These vouchers cannot overlap with existing promotions, do you want to replace them?'
        onConfirm={handleConfirmOverlap}
      />
    </>
  );
}
